import type { TFunction } from "i18next";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValidId } from "../../../u2000Core/tools/U2000IdUtils";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS } from "../../U3121";
import type { U3121GoogleCalendarSync, U3121GoogleCalendarSyncStatus as U3121GoogleCalendarSyncStatusValue, U3121ProjectToSurvey } from "../../U3121Service";
import { useU3121AdoptGoogleDueDateHandlers } from "../../U3121UseAdoptGoogleDueDateHandlers";

interface U3121GoogleCalendarSyncStatusProps {
  sync: U3121GoogleCalendarSync | null | undefined;
  t: TFunction;
  projectId?: string;
  subProjectId?: string;
  canAdopt?: boolean;
  formStateHandlers?: U2000FormStateHandlers<{ projectToSurvey?: U3121ProjectToSurvey | null }>;
}

// Alias renommé pour éviter la collision de nom avec le composant (bug TS1485 verbatimModuleSyntax + export default)
function badgeClass(status: U3121GoogleCalendarSyncStatusValue): string {
  switch (status) {
    case "SYNCED":
      return "bg-green-100 text-green-800";
    case "FAILED":
      return "bg-red-100 text-red-800";
    case "PENDING":
      return "bg-amber-100 text-amber-800";
    case "DELETED":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

function statusLabelKey(status: U3121GoogleCalendarSyncStatusValue): string {
  switch (status) {
    case "SYNCED":
      return "lblGoogleCalendarSyncSynced";
    case "FAILED":
      return "lblGoogleCalendarSyncFailed";
    case "PENDING":
      return "lblGoogleCalendarSyncPending";
    case "DELETED":
      return "lblGoogleCalendarSyncDeleted";
    default:
      return "lblGoogleCalendarSyncNone";
  }
}

function formatLastSyncAt(iso: string | null | undefined, locale: string): string | null {
  if (!iso?.trim()) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale.startsWith("fr") ? "fr-CA" : "en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function U3121GoogleCalendarSyncStatus({
  sync,
  t,
  projectId,
  subProjectId,
  canAdopt = false,
  formStateHandlers,
}: U3121GoogleCalendarSyncStatusProps) {
  const { i18n } = useTranslation();
  const { adoptGoogleDueDate, adoptSubProjectGoogleDueDate } = useU3121AdoptGoogleDueDateHandlers();
  const [isAdopting, setIsAdopting] = useState(false);
  const status = sync?.status;
  const divergence = sync?.dateDivergence;

  const handleAdopt = useCallback(async () => {
    const pid = projectId?.trim();
    if (!pid || !isValidId(pid) || !canAdopt || !formStateHandlers) return;
    setIsAdopting(true);
    try {
      const result =
        subProjectId && isValidId(subProjectId)
          ? await adoptSubProjectGoogleDueDate(pid, subProjectId)
          : await adoptGoogleDueDate(pid);
      if (!result) return;
      const current = formStateHandlers.getData();
      if (!current?.projectToSurvey) return;
      formStateHandlers.setData({
        ...current,
        projectToSurvey: {
          ...current.projectToSurvey,
          dueDate: result.dueDate,
          version: result.version,
        },
      });
    } finally {
      setIsAdopting(false);
    }
  }, [projectId, subProjectId, canAdopt, formStateHandlers, adoptGoogleDueDate, adoptSubProjectGoogleDueDate]);

  const showAdoptButton =
    divergence?.kind === "mismatch" && canAdopt && projectId && isValidId(projectId) && formStateHandlers;

  const divergenceBlock =
    divergence && divergence.message?.trim() ? (
      <div
        className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
        role="alert"
      >
        <p className="min-w-0 flex-1 basis-[min(100%,12rem)] leading-snug">{divergence.message}</p>
        {showAdoptButton && (
          <button
            type="button"
            className="ms-auto shrink-0 rounded-lg border border-primary-600 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
            disabled={isAdopting}
            onClick={() => void handleAdopt()}
          >
            {t("btnGoogleCalendarAdoptDueDate", { ns: NS })}
          </button>
        )}
      </div>
    ) : null;

  if (!status) {
    return (
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-700">{t("lblGoogleCalendarSync", { ns: NS })}</span>
        <p className="text-sm text-gray-500">{t("lblGoogleCalendarSyncNone", { ns: NS })}</p>
        {divergenceBlock}
      </div>
    );
  }

  const lastSyncFormatted = formatLastSyncAt(sync.lastSyncAt, i18n.language);
  const badgeTitle = status === "FAILED" && sync.lastError ? sync.lastError : undefined;

  return (
    <div className="space-y-1">
      <span className="text-sm font-medium text-gray-700">{t("lblGoogleCalendarSync", { ns: NS })}</span>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${badgeClass(status)}`} title={badgeTitle}>
          {t(statusLabelKey(status), { ns: NS })}
        </span>
        {lastSyncFormatted && (
          <span className="text-xs text-gray-500">{t("lblGoogleCalendarSyncLastAt", { ns: NS, date: lastSyncFormatted })}</span>
        )}
      </div>
      {divergenceBlock}
    </div>
  );
}

export default U3121GoogleCalendarSyncStatus;
